import { z } from "zod";
export const SNAPSHOT_MOVEMENT_STEP_GOAL_DEFAULT = 7500;
export const SNAPSHOT_MOVEMENT_STEP_GOAL_MIN = 1000;
// The same ceiling the step seam accepts (`PutStepGoalRequest`), since it is the same goal.
export const SNAPSHOT_MOVEMENT_STEP_GOAL_MAX = 100000;
export const snapshotStateSchema = z.enum(["fresh", "quiet", "one-card", "unsettled", "stale"]);
export const snapshotBehaviourIdSchema = z.enum([
    "sleep",
    "food",
    "movement",
    "stress",
    "connection",
]);
export const snapshotChartVizSchema = z.enum(["sparkline", "bars", "weekdays", "dots"]);
export const SNAPSHOT_STATUS_WORDS = [
    "strong",
    "steady",
    "holding",
    "lifting",
    "watch",
    "quiet",
];
// A defaulted/neutral score has no series behind it: directional words are earned by data.
// "quiet" rather than "watch": a behaviour nothing has reported on is not a
// behaviour going wrong, and a first-week member reading that all five need
// watching is being told off for data they have not had the chance to produce.
/** Below this a behaviour reads "watch", so it is the bar a score must earn. */
export const SNAPSHOT_WATCH_SCORE = 60;
export function snapshotStatusWord(score, defaulted, atFloor) {
    if (defaulted || atFloor)
        return "quiet";
    if (score >= 90)
        return "strong";
    if (score >= 80)
        return "steady";
    if (score >= 70)
        return "holding";
    if (score >= SNAPSHOT_WATCH_SCORE)
        return "lifting";
    return "watch";
}
export const snapshotMovementStepGoalSchema = z
    .number()
    .int()
    .min(SNAPSHOT_MOVEMENT_STEP_GOAL_MIN)
    .max(SNAPSHOT_MOVEMENT_STEP_GOAL_MAX);
export const snapshotReadingSchema = z.object({
    /**
     * A stable handle for the readings the narrative binds to, so the label
     * stays copy. Binding on the label meant renaming one silently unhooked the
     * number behind it, and only a test stood between that and a wrong figure
     * in the member's Snapshot.
     */
    key: z.string().min(1).max(40).optional(),
    label: z.string().min(1).max(40),
    value: z.string().min(1).max(40),
    source: z.string().min(1).max(60),
    bad: z.boolean().optional(),
});
export const snapshotScoreEntrySchema = z.object({
    score: z.number().int().min(0).max(100),
    /** "quiet" means ungraded: nothing reported, so `score` is a neutral base, not a reading. */
    status: z.enum(["ok", "watch", "quiet"]),
    value: z.string().min(1).max(40),
    /** True when the score is a defaulted/neutral provenance rather than a measured series. */
    defaulted: z.boolean().optional(),
    /** True when the score sits on its starting base with nothing completed yet. */
    atFloor: z.boolean().optional(),
});
export const snapshotRowSchema = z.object({
    readings: z.array(snapshotReadingSchema).min(1).max(6),
    /** Behaviours whose watch flag co-occurred with this one this week; computed, never static. */
    correlates: z.array(snapshotBehaviourIdSchema).max(4).optional(),
    /** One value per day, oldest first. Null is a day nobody reported, not a nought. */
    chart: z.array(z.number().nullable()).max(31).optional(),
    chartLabels: z.array(z.string().min(1).max(24)).max(31).optional(),
    chartUnit: z.string().min(1).max(20).optional(),
    viz: snapshotChartVizSchema.optional(),
    target: z.number().optional(),
    range: z.tuple([z.number(), z.number()]).optional(),
    chartMax: z.number().optional(),
    actionable: z.boolean().default(true),
    means: z.string().min(20).max(420),
});
export const snapshotSourceSchema = z.object({
    name: z.string().min(1).max(40),
    live: z.boolean(),
});
export const snapshotLastWeekWinSchema = z.object({
    icon: z.string().min(1).max(40),
    label: z.string().min(1).max(40),
    value: z.string().min(1).max(60),
});
export const snapshotOverallSchema = z.object({
    /** Null until at least one behaviour is graded; a mean of defaults is not a score. */
    score: z.number().int().min(0).max(100).nullable(),
    status: z.enum(["ok", "watch", "quiet"]),
    trend: z.string().min(1).max(8),
    /** One-line provenance for the score: which source produced it. */
    provenance: z.string().min(1).max(80).optional(),
});
export const snapshotPayloadSchema = z.object({
    state: snapshotStateSchema,
    persona: z.string().min(2).max(60),
    lifeContext: z.enum(["default", "low_agency"]),
    capturedAt: z.string(),
    staleSource: z.string().max(120).nullable(),
    sources: z.array(snapshotSourceSchema).min(0).max(8),
    lastWeekWins: z.array(snapshotLastWeekWinSchema).min(0).max(4),
    headline: z.string().min(4).max(120),
    overall: snapshotOverallSchema,
    // The note is the four-beat page summary; roomier than the row prose.
    note: z.string().min(20).max(560),
    quietNote: z.string().min(20).max(320).nullable(),
    scores: z.object({
        sleep: snapshotScoreEntrySchema,
        food: snapshotScoreEntrySchema,
        movement: snapshotScoreEntrySchema,
        stress: snapshotScoreEntrySchema,
        connection: snapshotScoreEntrySchema,
    }),
    rows: z.object({
        sleep: snapshotRowSchema,
        food: snapshotRowSchema,
        movement: snapshotRowSchema,
        stress: snapshotRowSchema,
        connection: snapshotRowSchema,
    }),
});
export const snapshotNarrativeRowSchema = z.object({
    means: z.string().min(20).max(420),
});
export const snapshotNarrativeDraftSchema = z.object({
    headline: z.string().min(4).max(120),
    note: z.string().min(20).max(560),
    quietNote: z.string().min(20).max(320),
    rows: z.object({
        sleep: snapshotNarrativeRowSchema,
        food: snapshotNarrativeRowSchema,
        movement: snapshotNarrativeRowSchema,
        stress: snapshotNarrativeRowSchema,
        connection: snapshotNarrativeRowSchema,
    }),
});
/** Which way a behaviour's own series is going, from the values the sparkline draws, so the mark beside a line cannot point against it. */
export function snapshotSeriesTrend(values) {
    // Compare the halves of the window, not of the reported days, so a gap in one half does not silently move the dividing line.
    const mid = Math.ceil(values.length / 2);
    const earlier = values.slice(0, mid).filter((v) => v !== null);
    const recent = values.slice(mid).filter((v) => v !== null);
    const reported = [...earlier, ...recent];
    if (reported.length < 4 || earlier.length === 0 || recent.length === 0)
        return "flat";
    const delta = median(recent) - median(earlier);
    const range = Math.max(...reported) - Math.min(...reported);
    // Two floors, because either alone gets it wrong.
    const deadBand = Math.max(range * 0.12, Math.abs(median(reported)) * 0.02);
    if (range === 0 || Math.abs(delta) <= deadBand)
        return "flat";
    return delta > 0 ? "up" : "down";
}
function median(xs) {
    const sorted = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1)
        return sorted[mid] ?? 0;
    return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}
//# sourceMappingURL=snapshot.js.map